import { useCallback, useEffect, useRef, useState } from 'react';
import Service from '../../services/VideoThumbnailService';
import { selectThumbnail } from './thumbnailFiles';

export default function useVideoThumbnail(kind, videoId) {
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(Boolean(videoId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const working = useRef(false);
  const generation = useRef(0);

  const reload = useCallback(async () => {
    const request = ++generation.current;
    if (!videoId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { thumbnail: saved } = await Service.get(kind, videoId);
      const blob = saved ? await Service.image(kind, videoId) : null;
      if (request === generation.current)
        setThumbnail(saved ? { ...saved, blob } : null);
    } catch (_) {
      if (request === generation.current)
        setError('Could not load the thumbnail. Try again.');
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [kind, videoId]);

  useEffect(() => {
    reload();
    const unsubscribe = Service.subscribe(kind, videoId, reload);
    return () => {
      generation.current += 1;
      unsubscribe();
    };
  }, [kind, videoId, reload]);

  async function change(files) {
    if (working.current || loading || !videoId) return;
    working.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (files === null) {
        await Service.remove(kind, videoId);
        setNotice(
          'Attachment removed. An image already sent to YouTube stays on that video.',
        );
      } else {
        const file = await selectThumbnail(files);
        await Service.save(kind, videoId, file);
        setNotice(
          'Thumbnail saved. It will upload with this video or update its existing YouTube upload.',
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          (err.isAxiosError
            ? 'Could not save the thumbnail. Try again.'
            : err.message),
      );
    } finally {
      working.current = false;
      setBusy(false);
    }
  }

  return {
    thumbnail,
    loading,
    busy,
    error,
    notice,
    reload,
    change,
    clearNotice: () => {
      setNotice('');
      setError('');
    },
  };
}
